
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ConsultationContext = createContext(null);

export const ConsultationProvider = ({ children }) => {
  const location = useLocation();
  const [pagesVisited, setPagesVisited] = useState([]);
  const [modelsViewed, setModelsViewed] = useState([]);
  const [zonesExplored, setZonesExplored] = useState([]);
  const startTimeRef = useRef(Date.now());

  // Track page visits
  useEffect(() => {
    const pageName = getPageName(location.pathname);
    setPagesVisited(prev => {
      if (prev[prev.length - 1] === pageName) return prev;
      return [...prev, pageName].slice(-20);
    });

    // Track model views from URL
    const modelMatch = location.pathname.match(/\/(models|showroom)\/([a-z-]+)$/);
    if (modelMatch && modelMatch[2] !== 'compare') {
      setModelsViewed(prev => {
        if (prev.includes(modelMatch[2])) return prev;
        return [...prev, modelMatch[2]];
      });
    }
  }, [location.pathname]);

  const trackZone = useCallback((zoneName) => {
    setZonesExplored(prev => {
      if (prev.includes(zoneName)) return prev;
      return [...prev, zoneName];
    });
  }, []);

  const getSessionSummary = useCallback(() => {
    const timeOnSite = Math.round((Date.now() - startTimeRef.current) / 1000 / 60);
    return {
      pages_visited: pagesVisited,
      models_viewed: modelsViewed,
      zones_explored: zonesExplored,
      time_on_site_minutes: timeOnSite,
      current_page: location.pathname,
    };
  }, [pagesVisited, modelsViewed, zonesExplored, location.pathname]);

  const getCurrentModel = useCallback(() => {
    const modelMatch = location.pathname.match(/\/(models|showroom)\/([a-z-]+)$/);
    if (modelMatch && modelMatch[2] !== 'compare') return modelMatch[2];
    return null;
  }, [location.pathname]);

  return (
    <ConsultationContext.Provider value={{ pagesVisited, modelsViewed, zonesExplored, trackZone, getSessionSummary, getCurrentModel }}>
      {children}
    </ConsultationContext.Provider>
  );
};

export const useConsultationContext = () => {
  const context = useContext(ConsultationContext);
  if (!context) throw new Error('useConsultationContext must be used within ConsultationProvider');
  return context;
};

function getPageName(pathname) {
  if (pathname === '/') return 'Home';
  const segments = pathname.split('/').filter(Boolean);
  return segments.map(s => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ')).join(' > ');
}
